import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FOOD_MENU } from "../data/menuFood";
import { DRINKS_MENU } from "../data/menuDrinks";
import { BAR_MENU } from "../data/menuBar";

const GROUPS = [
    { id: "food", label: "Food", data: FOOD_MENU, testid: "menu-group-food" },
    { id: "drinks", label: "Drinks", data: DRINKS_MENU, testid: "menu-group-drinks" },
    { id: "bar", label: "Bar", data: BAR_MENU, testid: "menu-group-bar" },
];

const CATEGORY_IMAGES = {
    "bowls": { src: "/gallery/DSC04143.webp", caption: "Bowls and spreads from the Esak kitchen", portrait: true },
    "quesadilla-rolls": { src: "/gallery/DSC04150.webp", caption: "Tandoori on a Roll, plated and proud", portrait: false },
    "sushi": { src: "/gallery/DSC04158.webp", caption: "Sushi rolls, fresh off the slate", portrait: true },
    "pasta": { src: "/gallery/DSC04149.webp", caption: "Pasta, twirled to order", portrait: false },
    "indian-small-plates": { src: "/gallery/1.webp", caption: "Small plates, big flavours", portrait: true },
    "sharing-platters": { src: "/gallery/DSC04142.webp", caption: "Made for the middle of the table", portrait: true },
    "continental-large-plates": { src: "/gallery/DSC04153.webp", caption: "Lasagne, layered with love", portrait: true },
    "chinese-mains": { src: "/gallery/DSC04144.webp", caption: "Wok-tossed and wok-fired", portrait: true },
    "desserts": { src: "/gallery/DSC05253.webp", caption: "Meetha, the Esak way", portrait: true },
    "esak-series": { src: "/gallery/DSC04118.webp", caption: "Sips from the summer series", portrait: false },
    "mocktails": { src: "/gallery/DSC04123.webp", caption: "Colour in every glass", portrait: false },
    "esak-signatures": { src: "/gallery/DSC05256.webp", caption: "Signatures, shaken and stirred", portrait: true },
    "classic-cocktails": { src: "/gallery/DSC04139.webp", caption: "Classics at the Avenue bar", portrait: true },
};

const VegDot = ({ nv }) => (
    <span
        className={`inline-block w-3.5 h-3.5 rounded-[4px] border-2 mt-1 shrink-0 ${nv ? "border-[#7A1F1F]" : "border-[#1E7A34]"}`}
        title={nv ? "Non-vegetarian" : "Vegetarian"}
    >
        <span className={`block w-1.5 h-1.5 rounded-full m-[3px] ${nv ? "bg-[#7A1F1F]" : "bg-[#1E7A34]"}`} />
    </span>
);

export const EsakMenu = () => {
    const [group, setGroup] = useState("food");
    const [category, setCategory] = useState(FOOD_MENU[0].id);

    const activeGroup = GROUPS.find((g) => g.id === group);
    const activeCat = activeGroup.data.find((c) => c.id === category) || activeGroup.data[0];
    const catImage = CATEGORY_IMAGES[activeCat.id];

    const switchGroup = (g) => {
        setGroup(g.id);
        setCategory(g.data[0].id);
    };

    const itemsList = (
        <ul className={catImage ? "grid gap-y-8" : "grid md:grid-cols-2 gap-x-14 gap-y-8"}>
            {activeCat.items.map((item) => (
                <li key={item.name} className="flex gap-3" data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <VegDot nv={item.nv} />
                    <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                            <h3 className="font-syne text-base font-bold text-[#1C1C1E]">{item.name}</h3>
                            <span className="flex-1 border-b border-dotted border-[#1C1C1E]/25 -translate-y-1" />
                            <span className="font-serif-brand text-base font-semibold whitespace-nowrap" style={{ color: "#E50046" }}>₹{item.price}</span>
                        </div>
                        {item.desc && <p className="mt-1 text-sm leading-relaxed text-[#1C1C1E]/55">{item.desc}</p>}
                    </div>
                </li>
            ))}
        </ul>
    );

    return (
        <section id="menu" className="py-24 md:py-32 px-6 md:px-10" style={{ backgroundColor: "#FFF1DC" }} data-testid="menu-section">
            <div className="max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center max-w-2xl mx-auto"
                >
                    <p className="font-serif-brand italic text-xl mb-3" style={{ color: "#E50046" }}>Crafted with love</p>
                    <h2 className="font-syne text-4xl md:text-5xl font-extrabold tracking-tight text-[#1C1C1E]" data-testid="menu-headline">
                        The Esak Menu
                    </h2>
                    <p className="mt-4 text-base text-[#1C1C1E]/60" data-testid="menu-subcopy">
                        Familiar flavours, a fresh twist, light, colourful and made to delight today's heart.
                    </p>
                </motion.div>

                <div className="mt-12 flex justify-center gap-3" data-testid="menu-groups">
                    {GROUPS.map((g) => (
                        <button
                            key={g.id}
                            onClick={() => switchGroup(g)}
                            data-testid={g.testid}
                            className={`rounded-full px-8 py-3 text-sm font-bold uppercase tracking-widest transition-[background-color,color,box-shadow] duration-200 ${
                                group === g.id
                                    ? "bg-[#1C1C1E] text-white shadow-lg"
                                    : "bg-white/70 text-[#1C1C1E]/60 hover:bg-white"
                            }`}
                        >
                            {g.label}
                        </button>
                    ))}
                </div>

                <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="menu-categories">
                    {activeGroup.data.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setCategory(cat.id)}
                            data-testid={`menu-tab-${cat.id}`}
                            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                                activeCat.id === cat.id
                                    ? "bg-[#E50046] text-white shadow-md"
                                    : "bg-white/60 text-[#1C1C1E]/60 hover:bg-white hover:text-[#1C1C1E]"
                            }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeCat.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.3 }}
                        className="mt-12"
                        data-testid={`menu-panel-${activeCat.id}`}
                    >
                        {activeCat.note && (
                            <p className="text-center text-sm font-medium mb-8" style={{ color: "#E50046" }} data-testid={`menu-note-${activeCat.id}`}>
                                {activeCat.note}
                            </p>
                        )}
                        {catImage ? (
                            <div className="grid lg:grid-cols-5 gap-10 items-start">
                                <div className="lg:col-span-2 lg:sticky lg:top-28">
                                    <div className="rounded-[2rem] overflow-hidden shadow-xl shadow-[#1C1C1E]/10" data-testid={`menu-category-image-${activeCat.id}`}>
                                        <img
                                            src={catImage.src}
                                            alt={catImage.caption}
                                            className={`w-full object-cover ${catImage.portrait ? "aspect-[3/4]" : "aspect-[4/3]"}`}
                                            loading="lazy"
                                        />
                                    </div>
                                    <p className="mt-3 text-center font-serif-brand italic text-sm text-[#1C1C1E]/55">{catImage.caption}</p>
                                </div>
                                <div className="lg:col-span-3">{itemsList}</div>
                            </div>
                        ) : (
                            itemsList
                        )}
                    </motion.div>
                </AnimatePresence>

                <p className="mt-16 text-center text-sm text-[#1C1C1E]/50" data-testid="menu-note">
                    Government taxes as applicable · Bar menu served to guests of legal drinking age only.
                </p>
            </div>
        </section>
    );
};
